import { Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Selection } from "react-aria-components";
import { useAsyncList } from "react-stately";

import {
  contactsApi,
  TitleResponse,
  useContactRolesQuery,
} from "@/api/contacts.api";
import { IconContainer } from "@/components/campaign/basic/IconContainer";
import { Button } from "@/components/generic/Button";
import { Checkbox } from "@/components/generic/Checkbox";
import { Heading } from "@/components/generic/Heading";
import { Modal } from "@/components/generic/Modal";
import { MultiSelect, MultiSelectItem } from "@/components/generic/MultiSelect";
import { Persona, type Configuration } from "@/types/campaign";
import { formatNumber } from "@/utils/number-utils";
import { twMerge } from "tailwind-merge";
import Badge from "../Badge";
import { TitlePersonaSelector } from "../campaign/TitlePersonaSelector";

interface AddPersonaModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSave: (persona: Persona) => void;
  configuration: Configuration;
  initialData?: Persona;
  personas: Persona[];
}

export function AddPersonaModal({
  isOpen,
  onOpenChange,
  onSave,
  configuration,
  initialData,
  personas,
}: AddPersonaModalProps) {
  const { data: rolesData } = useContactRolesQuery([configuration]);

  const [selectedRoles, setSelectedRoles] = useState<Selection>(() => {
    if (initialData?.levels.length) {
      return new Set(
        initialData.levels.map((l) => l.toLowerCase().replace(/\s+/g, "-")),
      );
    }
    return new Set();
  });

  const [selectedDepartments, setSelectedDepartments] = useState<Selection>(
    () => {
      if (initialData?.groups.length) {
        return new Set(
          initialData.groups.map((g) => g.toLowerCase().replace(/\s+/g, "-")),
        );
      }
      return new Set();
    },
  );

  const [selectedTitles, setSelectedTitles] = useState<
    Array<{ title: string; operator: "Equal" | "Contains" }>
  >(initialData?.titles ?? []);

  const [requirePhone, setRequirePhone] = useState(
    initialData?.requirePhone ?? false,
  );

  const [titleQuery, setTitleQuery] = useState("");

  const [isTitlePopoverOpen, setIsTitlePopoverOpen] = useState(false);

  useEffect(() => {
    if (initialData) {
      setSelectedRoles(
        new Set(
          initialData.levels.map((l) => l.toLowerCase().replace(/\s+/g, "-")),
        ),
      );
      setSelectedDepartments(
        new Set(
          initialData.groups.map((g) => g.toLowerCase().replace(/\s+/g, "-")),
        ),
      );
      setSelectedTitles(initialData.titles);
      setRequirePhone(initialData.requirePhone);
    } else {
      setSelectedRoles(new Set());
      setSelectedDepartments(new Set());
      setSelectedTitles([]);
      setRequirePhone(false);
    }
  }, [initialData]);
  const [cachedTitleItems, setCachedTitleItems] = useState<TitleResponse[]>([]);

  const availableRoles = useMemo<MultiSelectItem<string>[]>(() => {
    const levelCounts = new Map<string, number>();

    rolesData.forEach((role) => {
      if (role.level && role.group === null) {
        levelCounts.set(role.level, role.totalCount);
      }
    });

    const levelOrder = [
      "Direktør",
      "Head of",
      "Manager",
      "Partner",
      "Bestyrelse",
      "Ejer",
      "Stifter",
    ];

    return [...levelCounts.keys()]
      .sort((a, b) => {
        const aIdx = levelOrder.indexOf(a);
        const bIdx = levelOrder.indexOf(b);
        if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      })
      .filter((level) => levelOrder.includes(level))
      .map((level) => ({
        key: level.toLowerCase().replace(/\s+/g, "-"),
        label: `${level}|${formatNumber(levelCounts.get(level) ?? 0)}`,
        value: level,
      }));
  }, [rolesData]);

  const availableDepartments = useMemo<MultiSelectItem<string>[]>(() => {
    const groupCounts = new Map<string, number>();

    const selectedLevelValues =
      selectedRoles === "all"
        ? []
        : availableRoles
            .filter((r) => (selectedRoles as Set<string>).has(r.key))
            .map((r) => r.value);

    if (selectedLevelValues.length === 0) {
      rolesData.forEach((role) => {
        if (role.group && role.level === null) {
          groupCounts.set(role.group, role.totalCount);
        }
      });
    } else {
      rolesData.forEach((role) => {
        if (
          role.group &&
          role.level &&
          selectedLevelValues.includes(role.level)
        ) {
          const existing = groupCounts.get(role.group) ?? 0;
          groupCounts.set(role.group, existing + role.totalCount);
        }
      });
    }

    return [...groupCounts.keys()]
      .sort((a, b) => a.localeCompare(b, "da"))
      .map((group) => ({
        key: group.toLowerCase().replace(/\s+/g, "-"),
        label: `${group}|${formatNumber(groupCounts.get(group) ?? 0)}`,
        value: group,
      }));
  }, [rolesData, selectedRoles, availableRoles]);

  const allDepartmentsCount = useMemo(() => {
    const selectedLevelValues =
      selectedRoles === "all"
        ? []
        : availableRoles
            .filter((r) => (selectedRoles as Set<string>).has(r.key))
            .map((r) => r.value);

    if (selectedLevelValues.length === 0) {
      const total = rolesData.find((r) => r.level === null && r.group === null);
      return total?.totalCount ?? 0;
    } else {
      let sum = 0;
      rolesData.forEach((role) => {
        if (
          role.group === null &&
          role.level &&
          selectedLevelValues.includes(role.level)
        ) {
          sum += role.totalCount;
        }
      });
      return sum;
    }
  }, [rolesData, selectedRoles, availableRoles]);

  const selectedRoleValues = useMemo(() => {
    if (selectedRoles === "all") return availableRoles.map((r) => r.value);
    return availableRoles
      .filter((r) => selectedRoles.has(r.key))
      .map((r) => r.value);
  }, [selectedRoles, availableRoles]);

  const selectedDepartmentValues = useMemo(() => {
    if (selectedDepartments === "all")
      return availableDepartments.map((d) => d.value);
    return availableDepartments
      .filter((d) => selectedDepartments.has(d.key))
      .map((d) => d.value);
  }, [selectedDepartments, availableDepartments]);

  const titleList = useAsyncList<TitleResponse>({
    async load({ filterText, signal }) {
      try {
        setCachedTitleItems(titleList.items);
        const results = await contactsApi.getTitles(
          {
            title: filterText || "",
            titleOperator: "Contains",
            level:
              selectedRoleValues.length > 0 ? selectedRoleValues : undefined,
            group:
              selectedDepartmentValues.length > 0
                ? selectedDepartmentValues
                : undefined,
          },
          signal,
        );
        return { items: results };
      } catch {
        return { items: [] };
      }
    },
  });

  const handleAddTitle = useCallback(
    (title: string, operator: "Equal" | "Contains") => {
      setSelectedTitles((prev) => {
        if (prev.some((t) => t.title === title && t.operator === operator))
          return prev;
        return [...prev, { title, operator }];
      });
    },
    [],
  );

  const handleRemoveTitle = useCallback(
    (title: string, operator: "Equal" | "Contains") => {
      setSelectedTitles((prev) =>
        prev.filter((t) => !(t.title === title && t.operator === operator)),
      );
    },
    [],
  );

  const titleDuplicateCheck = useCallback(
    (data: {
      role: { level: string | null; group: string | null };
      title?: string | null;
      titleOperator?: "Equal" | "Contains";
    }) => {
      if (!data.title) return false;
      return selectedTitles.some(
        (t) => t.title === data.title && t.operator === data.titleOperator,
      );
    },
    [selectedTitles],
  );
  const personaDuplicateCheck = useCallback(() => {
    return personas?.some((persona) => {
      const levelsMatch =
        (persona.levels.length === selectedRoleValues.length &&
          persona.levels.every((level) =>
            selectedRoleValues.includes(level),
          )) ||
        selectedRoleValues.length === availableRoles.length;

      const groupsMatch =
        (persona.groups.length === selectedDepartmentValues.length &&
          persona.groups.every((group) =>
            selectedDepartmentValues.includes(group),
          )) ||
        selectedDepartmentValues.length === availableDepartments.length;

      const titlesMatch =
        persona.titles.length === (selectedTitles?.length ?? 0) &&
        persona.titles.every((title) =>
          selectedTitles?.some(
            (t) => t.title === title.title && t.operator === title.operator,
          ),
        );

      const phoneMatch = persona.requirePhone === requirePhone;

      return levelsMatch && groupsMatch && titlesMatch && phoneMatch;
    });
  }, [
    personas,
    selectedRoleValues,
    selectedDepartmentValues,
    selectedTitles,
    requirePhone,
    availableRoles,
    availableDepartments,
  ]);

  const resetFormState = useCallback(() => {
    setRequirePhone(false);
    setSelectedRoles(new Set());
    setSelectedDepartments(new Set());
    setSelectedTitles([]);
  }, []);

  const handleSave = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const root = document.getElementById("root");
    if (root?.hasAttribute("inert")) {
      root.removeAttribute("inert");
    }
    const persona: Persona = {
      levels: selectedRoleValues,
      groups: selectedDepartmentValues,
      titles: selectedTitles,
      requirePhone,
    };

    if (
      selectedRoleValues.length === 0 ||
      selectedRoleValues.length === availableRoles.length
    ) {
      persona.levels = [];
    }

    if (
      selectedDepartmentValues.length === 0 ||
      selectedDepartmentValues.length === availableDepartments.length
    ) {
      persona.groups = [];
    }

    onSave(persona);
  }, [
    selectedRoleValues,
    selectedDepartmentValues,
    selectedTitles,
    requirePhone,
    onSave,
    availableRoles,
    availableDepartments,
  ]);

  const handleCancel = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const root = document.getElementById("root");
    if (root?.hasAttribute("inert")) {
      root.removeAttribute("inert");
    }

    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    titleList.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoles, selectedDepartments]);

  useEffect(() => {
    if (!isOpen) {
      resetFormState();
    }
  }, [isOpen, resetFormState]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      aria-label={initialData ? "Rediger Persona" : "Tilføj Persona"}
      size="2xl"
      padding="lg"
      animation="fade"
      overlayClassName="data-[exiting]:pointer-events-none"
      closeButtonClassName={twMerge(
        isTitlePopoverOpen && "pointer-events-none",
      )}
    >
      <div className="flex w-full flex-col items-center gap-6">
        <IconContainer className="rounded-full p-3.5">
          <Users size={30} className="text-theme-active/80" />
        </IconContainer>

        <div className="flex flex-col gap-4">
          <Heading level={3} className="text-center">
            {initialData ? "Rediger Persona" : "Tilføj Persona"}
          </Heading>

          <p className="text-theme-on-glass-variant text-center text-sm">
            Vælg roller og afdelinger, som din persona skal matche.
            <br />
            Du kan også vælge specifikke titler.
          </p>
        </div>

        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-2">
            <MultiSelect
              label="Rolle"
              mode="compact"
              allTitle="Alle roller"
              items={availableRoles}
              selectedKeys={selectedRoles}
              onSelectionChange={setSelectedRoles}
              placeholder="Alle roller"
              buttonClassName="w-full"
            />
          </div>

          <div className="flex flex-col gap-2">
            <MultiSelect
              label="Afdelinger"
              mode="compact"
              allTitle="Alle afdelinger"
              allCount={allDepartmentsCount}
              items={availableDepartments}
              selectedKeys={selectedDepartments}
              onSelectionChange={setSelectedDepartments}
              placeholder="Alle afdelinger"
              buttonClassName="w-full"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-theme-on-glass-variant text-sm font-medium">
              Specifikke titler
            </label>
            <TitlePersonaSelector
              placeholder="Søg efter titler (f.eks. CMO)"
              list={titleList}
              query={titleQuery}
              setQuery={setTitleQuery}
              cachedItems={cachedTitleItems}
              onAddPersona={handleAddTitle}
              onRemovePersona={handleRemoveTitle}
              duplicateCheck={titleDuplicateCheck}
              currentRole={{ level: null, group: null }}
              selectedTitles={selectedTitles}
              mode="compact"
            >
              {selectedTitles.map((t) => (
                <span
                  key={`${t.title}-${t.operator}`}
                  onPointerDownCapture={(e) => e.stopPropagation()}
                >
                  <Badge
                    isDismissable
                    remove={() => handleRemoveTitle(t.title, t.operator)}
                  >
                    {t.operator === "Contains" ? `'${t.title}'` : t.title}
                  </Badge>
                </span>
              ))}
            </TitlePersonaSelector>
          </div>

          <Checkbox
            isSelected={requirePhone}
            onChange={setRequirePhone}
            boxClassName="  size-5!"
            className="text-theme-on-glass-variant"
          >
            Skal have direkte telefonnummer
          </Checkbox>
        </div>

        <div className="flex w-full justify-center gap-4 pt-0">
          <Button
            intent="secondary"
            className={twMerge(
              "min-w-28.5!",
              isTitlePopoverOpen && "pointer-events-none",
            )}
            onPress={handleCancel}
            animate
          >
            Annuller
          </Button>
          <Button
            intent="primary"
            className={twMerge(
              "min-w-28.5!",
              isTitlePopoverOpen && "pointer-events-none",
            )}
            onPress={handleSave}
            animate
            isDisabled={personaDuplicateCheck()}
          >
            {initialData ? "Opdater" : "Gem"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
